/**
 * Structured Data (JSON-LD) Generators
 * Creates Schema.org markup for improved SEO and rich snippets
 */

import { SEO_CONFIG } from './seoConfig';

/**
 * Organization Schema
 * Used on homepage for brand recognition
 */
export const generateOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SEO_CONFIG.siteName,
    url: SEO_CONFIG.siteUrl,
    logo: `${SEO_CONFIG.siteUrl}/logo192.png`,
    description: SEO_CONFIG.defaultDescription,
    sameAs: [
      // Add social media profiles here when available
      // 'https://twitter.com/cookwithotto',
      // 'https://facebook.com/cookwithotto',
    ],
  };
};

/**
 * Recipe Schema
 * Creates Schema.org Recipe structured data for rich snippets
 *
 * Note: This is prepared for future use when recipe pages become public
 */
interface RecipeData {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  ingredients?: Array<{ name: string; quantity: string; unit: string }>;
  steps?: Array<{ instruction: string; stepNumber: number }>;
  prepTime?: number; // in minutes
  cookTime?: number; // in minutes
  servings?: number;
  cuisine?: string;
  difficulty?: string;
  createdAt?: any;
}

export const generateRecipeSchema = (recipe: RecipeData) => {
  // Convert minutes to ISO 8601 duration format (e.g., "PT30M")
  const minutesToISO = (minutes?: number) => {
    if (!minutes) {
      return undefined;
    }
    return `PT${minutes}M`;
  };

  // Format ingredients as strings
  const ingredientStrings = recipe.ingredients?.map(
    (ing) => `${ing.quantity} ${ing.unit} ${ing.name}`.trim()
  ) || [];

  // Format instructions as HowToStep
  const instructionSteps = recipe.steps?.map((step, index) => ({
    '@type': 'HowToStep',
    text: step.instruction,
    position: step.stepNumber || index + 1,
  })) || [];

  const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);

  return {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.title,
    description: recipe.description || `Learn how to cook ${recipe.title} with Cook with Otto`,
    image: recipe.imageUrl ? [recipe.imageUrl] : undefined,
    author: {
      '@type': 'Organization',
      name: SEO_CONFIG.siteName,
    },
    datePublished: recipe.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
    prepTime: minutesToISO(recipe.prepTime),
    cookTime: minutesToISO(recipe.cookTime),
    totalTime: minutesToISO(totalTime),
    recipeYield: recipe.servings ? `${recipe.servings} servings` : undefined,
    recipeCategory: recipe.cuisine || 'General',
    recipeCuisine: recipe.cuisine,
    recipeIngredient: ingredientStrings.length > 0 ? ingredientStrings : undefined,
    recipeInstructions: instructionSteps.length > 0 ? instructionSteps : undefined,
    keywords: [recipe.cuisine, recipe.difficulty, 'cooking', 'recipe'].filter(Boolean).join(', '),
  };
};

/**
 * BreadcrumbList Schema
 * Helps search engines understand site structure
 */
interface BreadcrumbItem {
  name: string;
  url: string;
}

export const generateBreadcrumbSchema = (items: BreadcrumbItem[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${SEO_CONFIG.siteUrl}${item.url}`,
    })),
  };
};

/**
 * WebApplication Schema
 * Describes the app for search engines
 */
export const generateWebApplicationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: SEO_CONFIG.siteName,
    url: SEO_CONFIG.siteUrl,
    description: SEO_CONFIG.defaultDescription,
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Any',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '100',
    },
  };
};
